import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Heart, Sparkles, Users } from 'lucide-react'
import { supabase } from '../lib/supabase'
import SEO from '../components/SEO'

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
}

const defaultValues = [
  {
    icon: Heart,
    title: 'Made with Love',
    description:
      'Every teether is designed with the same care we would give our own children. Quality and safety are never compromised.',
  },
  {
    icon: Sparkles,
    title: 'Science-Backed',
    description:
      'Our products are developed in collaboration with pediatric therapists and developmental specialists.',
  },
  {
    icon: Users,
    title: 'Community First',
    description:
      'We listen to parents. Your feedback shapes our products and helps us better serve families.',
  },
]

const defaultContent = {
  hero_title: 'Our Story',
  hero_subtitle: 'Petite Palate was born from a simple observation: babies explore the world through their mouths, and every texture is a new discovery.',
  story_image: '',
  story_title: 'Where it all began',
  story_content: `As a new parent, I watched my daughter reach for everything—the corner of a blanket, a wooden spoon, the edge of a book. Each texture fascinated her, each surface a new world to explore.

But when it came to teethers, I found the same basic designs repeated endlessly: smooth, predictable, uninspiring. Where were the thoughtfully designed textures? Where was the connection to the real world of food and nature?

Petite Palate was created to fill that gap. We studied what families actually feed first — apple, banana, carrot — and designed each teether around those shapes. Real food forms with graduated textures that grow with your baby's development, chosen because familiarity with food shapes can support the transition to solids.`,
  mission_title: 'Our Mission',
  mission_content: "To create beautifully designed, developmentally-focused baby products that celebrate the joy of sensory exploration. We believe every first taste, every new texture, is a moment of wonder—and we're here to make those moments even more meaningful.",
  values: []
}

const iconMap = {
  0: Heart,
  1: Sparkles,
  2: Users
}

export default function About() {
  const [content, setContent] = useState(defaultContent)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchContent()
  }, [])

  const fetchContent = async () => {
    try {
      const { data, error } = await supabase
        .from('page_content')
        .select('content')
        .eq('page_slug', 'about')
        .single()

      if (data?.content) {
        setContent({ ...defaultContent, ...data.content })
      }
    } catch (error) {
      // Use defaults if table doesn't exist or no content
      console.log('Using default about content')
    } finally {
      setIsLoading(false)
    }
  }

  // Use custom values if set, otherwise use defaults
  const values = content.values && content.values.length > 0 && content.values[0].title
    ? content.values.map((v, i) => ({ ...v, icon: iconMap[i] || Heart }))
    : defaultValues

  if (isLoading) {
    return (
      <div className="min-h-screen bg-pp-ivory flex items-center justify-center">
        <div className="animate-pulse text-pp-stone">Loading...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-pp-ivory">
      <SEO
        title="About Us"
        description="Learn about Petite Palate's mission to support baby development through thoughtfully designed sensory teethers. Australian owned and operated."
        url="/about"
      />
      {/* Hero */}
      <section className="bg-pp-linen py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="font-display text-display-lg text-pp-ink mb-6">
              {content.hero_title}
            </h1>
            <p className="text-pp-stone text-lg leading-relaxed">
              {content.hero_subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              {content.story_image ? (
                <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                  <img
                    src={content.story_image}
                    alt="Our story"
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="aspect-[4/3] bg-pp-sand/30 rounded-2xl flex items-center justify-center">
                  <span className="text-pp-stone">Story Image</span>
                </div>
              )}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display text-display-md text-pp-ink mb-6">
                {content.story_title}
              </h2>
              <div className="space-y-4 text-pp-stone leading-relaxed">
                {content.story_content.split('\n\n').map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 lg:py-24 bg-pp-linen">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-display text-display-md text-pp-ink mb-4">
              What We Believe
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-pp-sand/50"
              >
                <div className="w-14 h-14 rounded-2xl bg-pp-sage-soft/50 flex items-center justify-center mb-6">
                  <value.icon className="w-7 h-7 text-pp-sage" />
                </div>
                <h3 className="font-display text-xl text-pp-ink mb-3">
                  {value.title}
                </h3>
                <p className="text-pp-stone text-sm leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="font-display text-display-md text-pp-ink mb-6">
              {content.mission_title}
            </h2>
            <p className="text-pp-stone text-lg leading-relaxed">
              {content.mission_content}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Australian Made */}
      <section className="py-16 lg:py-24 bg-pp-charcoal text-white">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-4xl mb-4">🇦🇺</p>
            <h2 className="font-display text-display-sm mb-4">
              Proudly Australian
            </h2>
            <p className="text-pp-sand max-w-lg mx-auto">
              Designed in Australia with love, manufactured to the highest safety
              standards, and shipped from Melbourne to families across the country.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  )
}
